import { AnimalHealthIndicator, AnimalType } from './animal';
import { HealthSystemDomainAnimalsResultAnimalSearchResult } from '../api/HealthSystemApi';

/**
 * One row of the `/animal-search` results table (`src/pages/AnimalSearch/component.tsx`).
 *
 * Mirrors `HealthSystemDomainAnimalsResultAnimalSearchResult`, trimmed to the fields the table and its
 * row renderers (`renderParentsCell`, `renderAnimalIsDied`) actually read. Everything else on the DTO -
 * weight, injection history, slaughtered date, plastic number, crossbreed, gender, weight category - is
 * dropped rather than carried around unused.
 */
export interface AnimalSearchResultRow {
  id: string;
  number: string;
  /** ISO date-time string; formatted in the component via `useFarmDateTime`, never here. */
  dateOfBirth: string;
  /** Absent renders as `'-'` in the cell. */
  genetics: string | null;
  /** Absent (no known mother/father) renders the parents cell as a plain dash. */
  motherId: string | null;
  motherNumber: string | null;
  fatherId: string | null;
  fatherNumber: string | null;
  /** Absent or zero renders as `'0'`. */
  childrenCount: number | null;
  animalType: AnimalType;
  healthIndicator: AnimalHealthIndicator;
  isDied: boolean;
  isSoftDeleted: boolean;
  /** Name of the disease attached to the animal's death, when there is one. */
  diseaseName: string | null;
  /** Name of the recorded mortality reason, when there is one. */
  mortalityReasonName: string | null;
  /** Free-text death reason, used only when neither `diseaseName` nor `mortalityReasonName` is set. */
  deathReason: string | null;
  /** ISO date-time string; formatted in the component, never here. */
  deathDate: string | null;
}

export const toAnimalSearchResultRow = (
  dto: HealthSystemDomainAnimalsResultAnimalSearchResult
): AnimalSearchResultRow => ({
  id: dto.id,
  number: dto.number,
  dateOfBirth: dto.dateOfBirth,
  genetics: dto.genetics ?? null,
  motherId: dto.motherId ?? null,
  motherNumber: dto.motherNumber ?? null,
  fatherId: dto.fatherId ?? null,
  fatherNumber: dto.fatherNumber ?? null,
  childrenCount: dto.childrenCount ?? null,
  animalType: dto.animalType,
  healthIndicator: dto.healthIndicator,
  isDied: dto.isDied,
  isSoftDeleted: dto.isSoftDeleted,
  diseaseName: dto.disease?.name ?? null,
  mortalityReasonName: dto.mortalityReason?.name ?? null,
  deathReason: dto.deathReason ?? null,
  deathDate: dto.deathDate ?? null
});
